import { of } from 'rxjs'
import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { catchError, map, share } from 'rxjs/operators'

import { environment } from '@env'
import { createTranslationKeys } from '@core/utils/main'
import { LazyParams } from '@shared/components/table/table.models'
import { SortingService } from '@core/services/sorting/sorting.service'

import { License } from './license.models'
import { LicenseFile } from './license.enums'
import { LICENSE_STATE_TRANSLATION } from './license.constants'
import { mapOptions } from '@core/utils/options.utils'

@Injectable()
export class LicenseService {
  readonly #http = inject(HttpClient)
  readonly #sortingService = inject(SortingService)

  getLicenses$(params = {}, lazyParams: LazyParams = {} as LazyParams) {
    const paramsBackEnd = {
      filtering: {
        freeText: '',
        ...params,
      },
      ...lazyParams,
    }
    return this.#http
      .post<{
        licenses: License[]
        [key: string]: any
      }>(environment.API.SEARCH_LICENSES, paramsBackEnd)
      .pipe(
        map((data) => ({
          ...data,
          paging: {
            totalRecords: data.licenses.length,
            ...data['paging'],
          },
          sorting: {
            ...data['sorting'],
            ...lazyParams.sorting,
          },
          licenses: this.#sortingService.sort(
            data.licenses.map((license) => ({
              ...license,
              requesterUser: {
                requesterName: license.requesterName,
                requesterEmail: license.requesterEmail,
              },
              ...createTranslationKeys(LICENSE_STATE_TRANSLATION, license, ['licenseState']),
              productShortName: `${license.productShortName} (${license.productName})`,
            })),
          ) as License[],
        })),
        share(),
        catchError(() => of({ licenses: [] as License[] })),
      )
  }

  getCustomersOptions$() {
    return this.getData$<{ id; name }[]>(environment.API.LICENSE_CUSTOMERS).pipe(
      map((data) => mapOptions(data)),
      catchError(() => of([])),
    )
  }

  getProductsOptions$() {
    return this.getData$<{ id; name }[]>(environment.API.LICENSE_PRODUCTS).pipe(
      map((data) => mapOptions(data)),
    )
  }

  getActiveProducts$(params) {
    return this.getData$<{ id; name; expirationDate }[]>(
      `${environment.API.LICENSE_PRODUCTS}/actives`,
      params,
    )
  }

  getStatesOptions$() {
    return this.getData$<{ licenseStates: string[] }>(environment.API.LICENSE_STATES).pipe(
      map(({ licenseStates }) =>
        licenseStates.map((licenseState) => ({
          label: LICENSE_STATE_TRANSLATION[licenseState] || licenseState,
          value: licenseState,
        })),
      ),
    )
  }

  getData$<T>(apiUrl, params?) {
    return this.#http.get<T>(apiUrl, { params })
  }

  newLicense(license) {
    return this.#http.post<any>(environment.API.CRUD_LICENSE, license)
  }

  getLicenceFileByType(id, type: LicenseFile) {
    return this.getData$(`${environment.API.LICENSE_DONGLE}/${id}/${type}`)
  }

  renewLicense(license) {
    return this.#http.put<any>(environment.API.LICENSE_RENEW, license)
  }

  rejectLicense(license) {
    return this.#http.put<any>(environment.API.LICENSE_REJECT, license)
  }

  syncLicenceStates() {
    return this.#http.put(environment.API.LICENSE_SYNC, {})
  }

  validateC2V(validateData) {
    return this.#http.put(environment.API.LICENSE_VALIDATE_C2V, validateData)
  }
}
